import { useState } from 'react'
import styles from './CTA.module.css'
import { sendContactEmail } from '../services/emailService'

const CTA = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    message: ''
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Send the contact form data to the email server
      const response = await sendContactEmail(formData)

      if (response.success) {
        console.log('CTA form submitted successfully:', response.reference)
        setSubmitStatus('success')

        // Clear the form on success
        setFormData({
          name: '',
          email: '',
          company: '',
          projectType: '',
          message: ''
        })
      } else {
        console.error('CTA form submission failed:', response.error)
        setSubmitStatus('error')
      }
    } catch (error) {
      console.error('CTA form error:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
      // Reset status message after 5 seconds
      setTimeout(() => setSubmitStatus('idle'), 5000)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <section id="contact" className={styles.section}>
      <div className={styles.background}>
        <div className={styles.blob1} />
        <div className={styles.blob2} />
        <div className={styles.blob3} />
      </div>

      <div className="container">
        <div className={styles.content}>
          <div className={styles.textSide}>
            <h2 className={styles.title}>
              Join{' '}
              <span className="gradient-text">the waitlist.</span>
            </h2>
            <p className={styles.subtitle}>
              Box Editor is in early access. We're onboarding teams who care about
              code quality — not just code speed. Tell us about your codebase
              and we'll be in touch.
            </p>
            <div className={styles.note}>
              The waitlist is limited — we review every request
            </div>
          </div>

          <div className={styles.formSide}>
            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label htmlFor="name" className={styles.label}>Full Name <span className={styles.required} aria-hidden="true">*</span></label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className={styles.input}
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>Email <span className={styles.required} aria-hidden="true">*</span></label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={styles.input}
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@company.com"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="company" className={styles.label}>Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  className={styles.input}
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Your Company Name"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="projectType" className={styles.label}>What are you working on? <span className={styles.required} aria-hidden="true">*</span></label>
                <select
                  id="projectType"
                  name="projectType"
                  className={styles.select}
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select one</option>
                  <option value="new-build">A new build</option>
                  <option value="existing-work">Something existing that needs work</option>
                  <option value="integration">An integration or extension</option>
                  <option value="not-sure">Not sure yet — still figuring it out</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="message" className={styles.label}>What's the problem you're trying to solve? <span className={styles.required} aria-hidden="true">*</span></label>
                <textarea
                  id="message"
                  name="message"
                  className={styles.textarea}
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Start wherever makes sense."
                />
              </div>

              <button
                type="submit"
                className={styles.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send message'}
              </button>

              {submitStatus === 'success' && (
                <div className={styles.successMessage}>
                  Got it. We'll read this carefully and be in touch.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className={styles.errorMessage}>
                  Something didn't go through. You can also reach us directly at contact@boxos.dev
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}

export default CTA
